/**
 * TripSharedLoadPanel — publish shared load to PWA / upcoming trips
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  MenuItem,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { InfoOutlined, OpenInNew, Public, PublicOff } from "@mui/icons-material";
import {
  fetchByInternalTripId,
  fetchSuggestedFields,
  publishFromInternalTrip,
  unpublishPublishedTrip,
  updatePublishedTrip,
} from "../../publishedTrips/services/publishedTripsService.js";
import { logError } from "../../../shared/utils/logger.js";
import { getPwaPublicBaseUrl } from "../../../shared/utils/pwaPublicUrl.js";
import { getSetting } from "../../systemSettings/services/systemSettingsService.js";

const OFFER_OPTIONS_KEY = "marketplace.sharedLoad.offerOptions";
const DEFAULT_OFFER_OPTIONS = ["Save 20%", "Save up to 40%"];

function parseOfferOptions(response) {
  const raw = response?.data?.value ?? response?.value ?? response?.data;
  if (!Array.isArray(raw)) return DEFAULT_OFFER_OPTIONS;
  const labels = raw
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
  return labels.length ? labels : DEFAULT_OFFER_OPTIONS;
}

const PWA_UPCOMING_PATH = "/upcoming-trips";

const LABELS_HELP_TOOLTIP =
  "The PWA shows one From and one To line for customers. Suggested labels use the first and last province in the corridor code (e.g. WC-GP-KZN → Western Cape → KwaZulu-Natal). Middle provinces are omitted — edit the labels or add detail in Departure window (e.g. “via Gauteng” or “WC → GP → KZN”).";

function corridorProvinceSegmentCount(routeCorridor) {
  if (!routeCorridor || typeof routeCorridor !== "string") return 0;
  return routeCorridor.split("-").filter(Boolean).length;
}

function getPwaUpcomingUrl() {
  const base = getPwaPublicBaseUrl();
  // Absolute PWA origin only — never relative (would open on admin host) or localhost in prod
  return base ? `${base}${PWA_UPCOMING_PATH}` : null;
}

const TripSharedLoadPanel = ({ internalTripId, routeCorridor, departureDate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [publishedTrip, setPublishedTrip] = useState(null);
  const [fromLabel, setFromLabel] = useState("");
  const [toLabel, setToLabel] = useState("");
  const [departureWindowText, setDepartureWindowText] = useState("");
  const [offerLabel, setOfferLabel] = useState("");
  const [publicNote, setPublicNote] = useState("");
  const [offerOptions, setOfferOptions] = useState(DEFAULT_OFFER_OPTIONS);
  const upcomingTripsUrl = getPwaUpcomingUrl();

  const load = useCallback(async () => {
    if (!internalTripId) return;
    setLoading(true);
    setError(null);
    try {
      let projection = await fetchByInternalTripId(internalTripId);
      if (!projection) {
        const suggested = await fetchSuggestedFields(internalTripId);
        setFromLabel(suggested.fromLabel || "");
        setToLabel(suggested.toLabel || "");
        setDepartureWindowText(suggested.departureWindowText || "");
        setOfferLabel("");
        setPublicNote("");
        setPublishedTrip(null);
      } else {
        setPublishedTrip(projection);
        setFromLabel(projection.fromLabel || "");
        setToLabel(projection.toLabel || "");
        setDepartureWindowText(projection.departureWindowText || "");
        setOfferLabel(projection.offerLabel || "");
        setPublicNote(projection.publicNote || "");
        if (
          !projection.fromLabel &&
          !projection.toLabel &&
          !projection.departureWindowText
        ) {
          const suggested = await fetchSuggestedFields(internalTripId);
          setFromLabel(suggested.fromLabel || "");
          setToLabel(suggested.toLabel || "");
          setDepartureWindowText(suggested.departureWindowText || "");
        }
      }
    } catch (err) {
      logError("TripSharedLoadPanel load failed", { error: err.message });
      setError(err.message || "Failed to load shared load settings");
    } finally {
      setLoading(false);
    }
  }, [internalTripId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    getSetting(OFFER_OPTIONS_KEY)
      .then((response) => {
        if (!cancelled) setOfferOptions(parseOfferOptions(response));
      })
      .catch(() => {
        if (!cancelled) setOfferOptions(DEFAULT_OFFER_OPTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isLive = Boolean(publishedTrip?.published);
  const departureDayPast =
    departureDate &&
    (() => {
      const fmt = (d) =>
        new Intl.DateTimeFormat("en-CA", {
          timeZone: "Africa/Johannesburg",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(new Date(d));
      return fmt(departureDate) < fmt(new Date());
    })();
  const previewRoute =
    fromLabel && toLabel
      ? `${fromLabel} → ${toLabel}`
      : routeCorridor || "Set route labels";
  const multiProvinceCorridor = corridorProvinceSegmentCount(routeCorridor) >= 3;

  const handlePublishToggle = async (checked) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (checked) {
        if (!fromLabel.trim() || !toLabel.trim()) {
          setError("From and To labels are required before publishing.");
          setSaving(false);
          return;
        }
        const data = await publishFromInternalTrip(internalTripId, {
          fromLabel: fromLabel.trim(),
          toLabel: toLabel.trim(),
          departureWindowText: departureWindowText.trim(),
          offerLabel: offerLabel.trim(),
          publicNote: publicNote.trim(),
        });
        setPublishedTrip(data);
        setSuccess("Shared load is now live on the PWA.");
      } else if (publishedTrip?.id) {
        await unpublishPublishedTrip(publishedTrip.id);
        setPublishedTrip((prev) => (prev ? { ...prev, published: false } : null));
        setSuccess("Shared load removed from the PWA.");
      }
    } catch (err) {
      setError(err.message || "Failed to update publish status");
    } finally {
      setSaving(false);
    }
  };

  const saveLabels = async ({ liveOnly }) => {
    if (!fromLabel.trim() || !toLabel.trim()) {
      setError("From and To labels are required.");
      return;
    }
    if (liveOnly && !isLive) {
      setError(
        'Turn on "Publish to PWA" to make this load visible on the customer site.'
      );
      return;
    }
    if (!publishedTrip?.id) {
      setError('Turn on "Publish to PWA" to create and publish the listing.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await updatePublishedTrip(publishedTrip.id, {
        fromLabel: fromLabel.trim(),
        toLabel: toLabel.trim(),
        departureWindowText: departureWindowText.trim(),
        offerLabel: offerLabel.trim(),
        publicNote: publicNote.trim(),
      });
      setPublishedTrip(data);
      setSuccess(
        isLive
          ? "Customer-facing copy saved."
          : "Draft copy saved. Turn on Publish to PWA when ready."
      );
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleFillSuggested = async () => {
    setSaving(true);
    setError(null);
    try {
      const suggested = await fetchSuggestedFields(internalTripId);
      setFromLabel(suggested.fromLabel || "");
      setToLabel(suggested.toLabel || "");
      setDepartureWindowText(suggested.departureWindowText || "");
    } catch (err) {
      setError(err.message || "Could not load suggestions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={28} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="h6">Customer-facing shared load</Typography>
              <Tooltip title={LABELS_HELP_TOOLTIP} arrow placement="top">
                <IconButton
                  size="small"
                  aria-label="How customer route labels work"
                  sx={{ color: "text.secondary" }}
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Chip
              icon={isLive ? <Public /> : <PublicOff />}
              label={isLive ? "Live on PWA" : "Not on PWA"}
              color={isLive ? "success" : "default"}
              size="small"
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Controls what customers see under Upcoming Shared Loads. Internal trip:{" "}
            <strong>{routeCorridor}</strong>
            {departureDate
              ? ` · ${new Date(departureDate).toLocaleDateString("en-ZA")}`
              : ""}
          </Typography>

          {!isLive && !loading && (
            <Alert severity="warning">
              This trip is <strong>not on the PWA</strong> yet. Turn on{" "}
              <strong>Publish to PWA</strong> below — saving copy alone does not
              list it under Upcoming Shared Loads.
            </Alert>
          )}
          {isLive && publishedTrip?.status === "completed" && (
            <Alert severity="warning">
              This listing is marked <strong>completed</strong> and is hidden
              from the PWA. Toggle publish off and on again, or set the trip
              departure to today or a future date.
            </Alert>
          )}
          {isLive && departureDayPast && (
            <Alert severity="warning">
              Departure day has passed. The PWA hides loads after their
              departure calendar day — edit the trip to a future date.
            </Alert>
          )}
          {multiProvinceCorridor && (
            <Alert severity="info" icon={<InfoOutlined />}>
              Corridor <strong>{routeCorridor}</strong> passes through{" "}
              {corridorProvinceSegmentCount(routeCorridor)} provinces. Customers only
              see <strong>From</strong> and <strong>To</strong> on the PWA — suggested
              labels use the <strong>first and last</strong> province only. To mention
              stops in between, edit the labels (e.g. To: “KwaZulu-Natal via Gauteng”)
              or add “WC → GP → KZN” in <strong>Departure window</strong>.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              PWA preview
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {previewRoute}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {departureWindowText || "Add departure window text"}
            </Typography>
            {offerLabel ? (
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: "#b8860b" }}>
                {offerLabel}
              </Typography>
            ) : null}
            {publicNote ? (
              <Typography variant="body2" sx={{ mt: 0.75, fontStyle: "italic" }}>
                {publicNote}
              </Typography>
            ) : null}
          </Box>

          <TextField
            label="From label"
            value={fromLabel}
            onChange={(e) => setFromLabel(e.target.value)}
            fullWidth
            size="small"
            required
            helperText="Start of the route shown to customers"
          />
          <TextField
            label="To label"
            value={toLabel}
            onChange={(e) => setToLabel(e.target.value)}
            fullWidth
            size="small"
            required
            helperText={
              multiProvinceCorridor
                ? "End of the route — include via/stops here if needed"
                : "End of the route shown to customers"
            }
          />
          <TextField
            label="Departure window"
            value={departureWindowText}
            onChange={(e) => setDepartureWindowText(e.target.value)}
            fullWidth
            size="small"
            placeholder={
              multiProvinceCorridor
                ? "e.g. Wed, 03 Jun 2026 · WC → GP → KZN"
                : "e.g. Departing 12–14 June"
            }
            helperText={
              multiProvinceCorridor
                ? "Optional: list all provinces or stops here"
                : "Shown under the route on the PWA"
            }
          />

          <TextField
            label="Customer note"
            value={publicNote}
            onChange={(e) => setPublicNote(e.target.value.slice(0, 280))}
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            placeholder="e.g. Space left for 2–3 m³ near Durban — book early"
            helperText={`${publicNote.length}/280 · Shown on the trip page. Internal trip notes stay private.`}
          />

          <TextField
            select
            label="Offer"
            value={offerLabel}
            onChange={(e) => setOfferLabel(e.target.value)}
            fullWidth
            size="small"
            helperText="Shown on the trip page. No offer leaves the badge off."
          >
            <MenuItem value="">No offer</MenuItem>
            {offerOptions.map((label) => (
              <MenuItem key={label} value={label}>
                {label}
              </MenuItem>
            ))}
            {offerLabel && !offerOptions.includes(offerLabel) ? (
              <MenuItem value={offerLabel}>{offerLabel}</MenuItem>
            ) : null}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={isLive}
                onChange={(e) => handlePublishToggle(e.target.checked)}
                disabled={saving}
              />
            }
            label={isLive ? "Published" : "Publish to PWA"}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Tooltip title={LABELS_HELP_TOOLTIP} arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleFillSuggested}
                  disabled={saving}
                >
                  Fill suggested labels
                </Button>
              </span>
            </Tooltip>
            {publishedTrip?.id && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => saveLabels({ liveOnly: false })}
                disabled={saving}
              >
                Save draft copy
              </Button>
            )}
            {isLive && publishedTrip?.id && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => saveLabels({ liveOnly: true })}
                disabled={saving}
              >
                Save copy
              </Button>
            )}
            {upcomingTripsUrl ? (
              <Button
                component={Link}
                href={upcomingTripsUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                endIcon={<OpenInNew />}
              >
                View upcoming trips
              </Button>
            ) : (
              <Tooltip title="Set VITE_PWA_PUBLIC_URL (admin build) to the public PWA origin, e.g. https://me.bubakii.co.za">
                <span>
                  <Button size="small" endIcon={<OpenInNew />} disabled>
                    View upcoming trips
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>

          {publishedTrip?.id && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                Published trip ID: {publishedTrip.id}
              </Typography>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TripSharedLoadPanel;
